import { Box, Theme, Typography, useMediaQuery } from "@mui/material";
import { ISelectOption } from "types/global";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo } from "react";
import { numberFormatter } from "utils/Formatter/Global";
import ControlledNumberTextField from "components/Controller/ControlledNumberTextField";
import ControlledSelect from "components/Controller/ControlledSelect";
import { useDisable } from "hooks/use-disable";
import { AdditionalDiscountType } from "generated/sales";
import { VatType } from "types/Sales/billing-note";

interface Props {
  documentType?: string;
}

const additionalDiscountTypeOptions: ISelectOption[] = [
  { label: "บาท", value: AdditionalDiscountType.Baht },
  { label: "%", value: AdditionalDiscountType.Percent },
];

const vatTypeOptions: ISelectOption[] = [
  { label: "ไม่มี", value: VatType.NoVat },
  { label: "0", value: VatType.Vat0 },
  { label: "7", value: VatType.Vat7 },
];

const NewBillingNoteSummary = ({ documentType }: Props) => {
  const { control, resetField, setValue } = useFormContext();
  const [disabled] = useDisable();
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md"),
  );
  const { t } = useTranslation();

  // ───────────────── WATCH ─────────────────
  const deliveryTripItemList = useWatch({
    control,
    name: "delivery_trip_item_list",
  });
  const discount = useWatch({ control, name: "discount" });
  const discount_type = useWatch({ control, name: "discount_type" });
  const vat_type = useWatch({ control, name: "vat_type" });
  const withholding_tax_percent = useWatch({
    control,
    name: "withholding_tax_percent",
  });

  // ───────────────── CALCULATION ─────────────────
  const pre_discount_amount = useMemo(() => {
    if (!deliveryTripItemList?.length) return 0;
    return (deliveryTripItemList as any[]).reduce((dtSum, dt) => {
      if (!dt?.income_list?.length) return dtSum;
      return (
        dtSum +
        (dt.income_list as any[]).reduce(
          (s: number, income: any) =>
            s + (income?.income_item_pre_vat_amount ?? 0),
          0,
        )
      );
    }, 0);
  }, [deliveryTripItemList]);

  const discount_thb = useMemo(() => {
    const val = discount || 0;
    return discount_type === AdditionalDiscountType.Percent
      ? (val / 100) * pre_discount_amount
      : val;
  }, [discount, discount_type, pre_discount_amount]);

  const post_discount_amount = pre_discount_amount - discount_thb;

  const vat_amount =
    vat_type === VatType.Vat7 ? 0.07 * post_discount_amount : 0;

  const net_amount = post_discount_amount + vat_amount;

  const withholding_tax_amount =
    ((withholding_tax_percent || 0) / 100) * post_discount_amount;

  const total_amount = net_amount - withholding_tax_amount;

  // ───────────────── SYNC ─────────────────
  useEffect(() => {
    setValue("pre_discount_amount", pre_discount_amount);
    setValue("post_discount_amount", post_discount_amount);
    setValue("vat_amount", vat_amount);
    setValue("net_amount", net_amount);
    setValue("withholding_tax_amount", withholding_tax_amount);
    setValue("total_amount", total_amount);
  }, [
    pre_discount_amount,
    post_discount_amount,
    vat_amount,
    net_amount,
    withholding_tax_amount,
    total_amount,
    setValue,
  ]);

  // ───────────────── GRID LAYOUT ─────────────────
  const ROW_GRID = {
    display: "grid",
    gridTemplateColumns: isMobile ? "1fr" : "210px 1fr 70px", // 👈 FIX LABEL WIDTH HERE
    alignItems: "center",
    columnGap: 3,
  };

  // ───────────────── COMPONENTS ─────────────────
  const ReadOnlyRow = ({
    label,
    amount,
    highlight = false,
  }: {
    label: string;
    amount: number;
    highlight?: boolean;
  }) => (
    <Box
      sx={{
        ...ROW_GRID,
        mb: 1.5,
        py: highlight ? 1 : 0,
      }}
      bgcolor={highlight ? "secondary.main" : undefined}
      borderRadius={1}
    >
      <Typography
        color="primary.main"
        textAlign={isMobile ? "left" : "right"}
        fontWeight={highlight ? 600 : 400}
      >
        {label}
      </Typography>

      {!isMobile && (
        <>
          <Typography
            color="primary.main"
            textAlign="right"
            fontWeight={highlight ? 600 : 400}
            mr={disabled ? undefined : 2.8}
          >
            {numberFormatter(amount)}
          </Typography>
          <Typography
            color="primary.main"
            fontWeight={highlight ? 600 : 400}
            ml={disabled ? undefined : 1.8}
          >
            บาท
          </Typography>
        </>
      )}
    </Box>
  );

  const SubAmountRow = ({ amount }: { amount: number }) => (
    <Box sx={{ ...ROW_GRID, mb: 1.5 }}>
      <Box />
      {!isMobile && (
        <>
          <Typography
            color="primary.main"
            textAlign="right"
            mr={disabled ? undefined : 2.8}
          >
            {numberFormatter(amount)}
          </Typography>
          <Typography color="primary.main" ml={disabled ? undefined : 1.8}>
            บาท
          </Typography>
        </>
      )}
    </Box>
  );

  const isPercentDiscount = discount_type === AdditionalDiscountType.Percent;

  const vatLabel =
    vatTypeOptions.find((o) => o.value === vat_type)?.label ?? "";

  // ───────────────── RENDER ─────────────────
  return (
    <>
      {/* Discount */}
      <Box sx={{ ...ROW_GRID, mb: 1.5 }}>
        <Typography
          color="primary.main"
          textAlign={isMobile ? "left" : "right"}
        >
          {t("price.discount")} (แยกภาษี)
        </Typography>

        {!isMobile && (
          <>
            {disabled ? (
              <Typography textAlign="right" color={"primary.main"}>
                {numberFormatter(discount || 0)}
              </Typography>
            ) : (
              <ControlledNumberTextField
                name="discount"
                control={control}
                textAlign="right"
                fullWidth
              />
            )}

            {disabled ? (
              <Typography color={"primary.main"}>
                {isPercentDiscount ? "%" : "บาท"}
              </Typography>
            ) : (
              <ControlledSelect
                control={control}
                name="discount_type"
                options={additionalDiscountTypeOptions}
                onChange={() => resetField?.("discount", { defaultValue: 0 })}
              />
            )}
          </>
        )}
      </Box>

      {isPercentDiscount && <SubAmountRow amount={discount_thb} />}

      {/* Post Discount */}
      <ReadOnlyRow
        label={t("price.post_discount_amount")}
        amount={post_discount_amount}
      />

      {/* VAT */}
      <Box sx={{ ...ROW_GRID, mb: 1.5 }}>
        <Typography
          color="primary.main"
          textAlign={isMobile ? "left" : "right"}
        >
          {t("price.vat_amount")}
        </Typography>

        {!isMobile && (
          <>
            {disabled ? (
              <Typography textAlign="right" color={"primary.main"}>
                {vatLabel}
              </Typography>
            ) : (
              <ControlledSelect
                control={control}
                name="vat_type"
                options={vatTypeOptions}
                sx={{
                  "& .MuiSelect-select": {
                    textAlign: "right",
                  },
                }}
              />
            )}
            <Typography color={"primary.main"} ml={disabled ? undefined : 1.8}>
              %
            </Typography>
          </>
        )}
      </Box>

      <SubAmountRow amount={vat_amount} />

      {/* Net */}
      <ReadOnlyRow
        label={t("price.net_amount")}
        amount={net_amount}
        highlight
      />

      {/* Withholding */}
      <Box sx={{ ...ROW_GRID, mb: 1.5 }}>
        <Typography
          color="primary.main"
          textAlign={isMobile ? "left" : "right"}
        >
          {t("price.withholding_tax_amount")}
        </Typography>

        {!isMobile && (
          <>
            {disabled ? (
              <Typography textAlign="right" color={"primary.main"}>
                {numberFormatter(withholding_tax_percent || 0)}
              </Typography>
            ) : (
              <ControlledNumberTextField
                name="withholding_tax_percent"
                control={control}
                textAlign="right"
                fullWidth
              />
            )}
            <Typography color={"primary.main"} ml={disabled ? undefined : 1.8}>
              %
            </Typography>
          </>
        )}
      </Box>

      <SubAmountRow amount={withholding_tax_amount} />

      {/* Total */}
      <ReadOnlyRow label={t("price.total_amount")} amount={total_amount} />
    </>
  );
};

export default NewBillingNoteSummary;
